/**
 * Graphique SVG aire dégradée sur la variable active (T° / pluie / vent / hum.).
 *
 * Construction :
 *  - Axe Y : bornes config (pluie, vent) ou auto-scale (T°, humidité), 4 graduations.
 *  - Axe X : un point par heure (168 sur 7 jours), séparateurs verticaux par jour.
 *  - Courbe : `M`/`L` paths SVG ; aire = même path fermé vers le bas.
 *  - Markers : "maintenant" (anthracite discret, masqué si curseur dessus) et
 *    "curseur d'exploration" (couleur variable + pill numérique).
 *  - Interaction : pointer events (souris + tactile), `touch-action: none`
 *    empêche le scroll page d'absorber le drag horizontal.
 */

import { useCallback, useMemo, useRef } from "react";

import {
  JOURS_COURT,
  SVG_HEIGHT,
  SVG_PAD_BOTTOM,
  SVG_PAD_LEFT,
  SVG_PAD_RIGHT,
  SVG_PAD_TOP,
  VARIABLES,
} from "./constants.js";
import { calculerBornesY } from "./formatters.js";
import { MeteoChartMarkers } from "./MeteoChartMarkers.js";
import type { MeteoPoint, VariableMeteo } from "./types.js";
import { useResizeWidth } from "./use-resize-width.js";

export function MeteoGraphique({
  hourly,
  indexMaintenant,
  indexCurseur,
  onChangeCurseur,
  variableActive,
}: {
  hourly: MeteoPoint[];
  indexMaintenant: number;
  indexCurseur: number;
  onChangeCurseur: (index: number) => void;
  variableActive: VariableMeteo;
}) {
  const [refContainer, widthMesurée] = useResizeWidth<HTMLDivElement>();
  const config = VARIABLES[variableActive];

  // Bornes Y : config explicite ou auto-scale sur la fenêtre 7j (cf formatters).
  const { yMin, yMax } = useMemo(() => calculerBornesY(hourly, config), [hourly, config]);

  const innerWidth = Math.max(60, widthMesurée - SVG_PAD_LEFT - SVG_PAD_RIGHT);
  const innerHeight = SVG_HEIGHT - SVG_PAD_TOP - SVG_PAD_BOTTOM;
  const nbPoints = hourly.length;

  const xFromIndex = useCallback(
    (i: number) => SVG_PAD_LEFT + (nbPoints <= 1 ? 0 : (i / (nbPoints - 1)) * innerWidth),
    [innerWidth, nbPoints],
  );
  const yFromValue = useCallback(
    (v: number) => {
      const clamped = Math.max(yMin, Math.min(yMax, v));
      const ratio = (clamped - yMin) / Math.max(1, yMax - yMin);
      return SVG_PAD_TOP + innerHeight * (1 - ratio);
    },
    [yMin, yMax, innerHeight],
  );

  // Construit les paths : ligne courbe + aire fermée vers le bas.
  const { pathLigne, pathAire } = useMemo(() => {
    if (hourly.length === 0) return { pathLigne: "", pathAire: "" };
    const segments: string[] = [];
    let premier = true;
    for (let i = 0; i < hourly.length; i++) {
      const p = hourly[i];
      if (!p) continue;
      const x = xFromIndex(i).toFixed(2);
      const y = yFromValue(config.extracteur(p)).toFixed(2);
      segments.push(`${premier ? "M" : "L"} ${x} ${y}`);
      premier = false;
    }
    const ligne = segments.join(" ");
    const yBas = (SVG_HEIGHT - SVG_PAD_BOTTOM).toFixed(2);
    const xPremier = xFromIndex(0).toFixed(2);
    const xDernier = xFromIndex(hourly.length - 1).toFixed(2);
    const aire = `${ligne} L ${xDernier} ${yBas} L ${xPremier} ${yBas} Z`;
    return { pathLigne: ligne, pathAire: aire };
  }, [hourly, xFromIndex, yFromValue, config]);

  // Séparateurs jours : 1 ligne verticale au premier point de chaque jour.
  const separateurs = useMemo(() => {
    const result: Array<{ x: number }> = [];
    let dernierJour = -1;
    for (let i = 0; i < hourly.length; i++) {
      const p = hourly[i];
      if (!p) continue;
      const jour = new Date(p.time).getDay();
      if (jour !== dernierJour && i > 0) {
        result.push({ x: xFromIndex(i) });
        dernierJour = jour;
      } else if (dernierJour === -1) {
        dernierJour = jour;
      }
    }
    return result;
  }, [hourly, xFromIndex]);

  // Graduations Y (4 niveaux entre yMin et yMax).
  // Labels = valeur numérique brute uniquement. L'unité est exprimée par la
  // légende (sous le graphique) — la dupliquer 4 fois sur l'axe Y déborderait
  // du `SVG_PAD_LEFT` étroit.
  const graduationsY = useMemo(() => {
    const result: Array<{ y: number; label: string }> = [];
    const step = (yMax - yMin) / 4;
    for (let i = 1; i <= 4; i++) {
      const v = yMin + step * i;
      result.push({ y: yFromValue(v), label: `${Math.round(v)}` });
    }
    return result;
  }, [yMin, yMax, yFromValue]);

  // Plages [first, last] de chaque jour distinct, pour centrer les libellés
  // X entre les séparateurs.
  const plagesJour = useMemo(() => {
    const plages: Array<{ first: number; last: number; label: string }> = [];
    for (let i = 0; i < hourly.length; i++) {
      const p = hourly[i];
      if (!p) continue;
      const d = new Date(p.time);
      const label = JOURS_COURT[d.getDay()] ?? "";
      const courant = plages[plages.length - 1];
      if (!courant || courant.label !== label) {
        plages.push({ first: i, last: i, label });
      } else {
        courant.last = i;
      }
    }
    return plages;
  }, [hourly]);

  // Convertit l'abscisse écran (px depuis le bord gauche du SVG) en index horaire.
  // `getBoundingClientRect().width` peut différer de `widthMesurée` (zoom du
  // navigateur, transformations CSS) — on applique le ratio pour rester précis.
  const indexFromX = useCallback(
    (xPx: number, rectWidth: number): number => {
      // Guards : `rectWidth <= 0` peut arriver pendant une frame où le SVG
      // est masqué (display:none transitoire) → division par 0 → NaN.
      if (nbPoints <= 1 || rectWidth <= 0 || innerWidth <= 0) return 0;
      const xSvg = (xPx / rectWidth) * widthMesurée;
      const xLocal = Math.max(0, Math.min(innerWidth, xSvg - SVG_PAD_LEFT));
      return Math.round((xLocal / innerWidth) * (nbPoints - 1));
    },
    [innerWidth, nbPoints, widthMesurée],
  );

  // Drag handlers — pointer events couvrent souris + tactile uniformément.
  const svgRef = useRef<SVGSVGElement>(null);
  const draggingRef = useRef(false);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!draggingRef.current) return;
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      onChangeCurseur(indexFromX(e.clientX - rect.left, rect.width));
    },
    [indexFromX, onChangeCurseur],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      draggingRef.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      // Tap = curseur saute immédiatement à la position (pas besoin de drag).
      handlePointerMove(e);
    },
    [handlePointerMove],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    draggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  // ID unique pour les gradients (évite collisions si plusieurs widgets dans le DOM).
  const gradientId = `meteo-area-${variableActive}`;

  return (
    <div ref={refContainer} className="w-full">
      <svg
        ref={svgRef}
        width={widthMesurée}
        height={SVG_HEIGHT}
        role="img"
        aria-label={`Graphique ${config.libelleLegende}. Tapez ou faites glisser pour explorer une heure précise.`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        // `touch-action: none` empêche le scroll vertical de la page d'absorber
        // notre geste horizontal de drag.
        style={{ display: "block", touchAction: "none", cursor: "ew-resize" }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={config.couleur} stopOpacity={config.opaciteHaut} />
            <stop offset="1" stopColor={config.couleur} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* Fond zone graphique : rectangle crème + bordure */}
        <rect
          x={SVG_PAD_LEFT}
          y={SVG_PAD_TOP}
          width={innerWidth}
          height={innerHeight}
          fill="#fdfbf3"
          stroke="#E5E0D2"
          strokeWidth={1}
          rx={6}
        />

        {/* Graduations Y */}
        {graduationsY.map((g, i) => (
          <g key={i}>
            <line
              x1={SVG_PAD_LEFT}
              x2={widthMesurée - SVG_PAD_RIGHT}
              y1={g.y}
              y2={g.y}
              stroke="rgba(56, 42, 20, 0.08)"
              strokeDasharray="2 3"
            />
            <text
              x={SVG_PAD_LEFT - 6}
              y={g.y + 3}
              textAnchor="end"
              fontSize={9.5}
              fontFamily="ui-monospace, monospace"
              fill="#8a7c5a"
            >
              {g.label}
            </text>
          </g>
        ))}

        {/* Séparateurs jours */}
        {separateurs.map((s, i) => (
          <line
            key={i}
            x1={s.x}
            x2={s.x}
            y1={SVG_PAD_TOP}
            y2={SVG_HEIGHT - SVG_PAD_BOTTOM}
            stroke="rgba(56, 42, 20, 0.06)"
            strokeWidth={1}
          />
        ))}

        {/* Aire dégradée */}
        <path d={pathAire} fill={`url(#${gradientId})`} />

        {/* Ligne courbe */}
        <path
          d={pathLigne}
          fill="none"
          stroke={config.couleur}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        <MeteoChartMarkers
          hourly={hourly}
          indexMaintenant={indexMaintenant}
          indexCurseur={indexCurseur}
          variableActive={variableActive}
          xFromIndex={xFromIndex}
          yFromValue={yFromValue}
          widthMesurée={widthMesurée}
        />

        {/* Libellés des jours en bas — CENTRÉS entre les séparateurs. */}
        {plagesJour.map((pl, i) => {
          const xCentre = (xFromIndex(pl.first) + xFromIndex(pl.last)) / 2;
          return (
            <text
              key={i}
              x={xCentre}
              y={SVG_HEIGHT - SVG_PAD_BOTTOM + 16}
              textAnchor="middle"
              fontSize={10}
              fontFamily="ui-sans-serif, system-ui"
              fill="#8a7c5a"
              fontWeight={600}
              letterSpacing="0.04em"
            >
              {pl.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
